import { useCallback, useEffect, useState } from 'react';

type VotingMode = 'open' | 'cookie' | 'tracked';

const SESSION_KEY = (projectId: string) => `fr_uid_open_${projectId}`;
const COOKIE_KEY = (projectId: string) => `fr_uid_cookie_${projectId}`;

function readSession(projectId: string): string | null {
	return sessionStorage.getItem(SESSION_KEY(projectId));
}

function writeSession(projectId: string, id: string) {
	sessionStorage.setItem(SESSION_KEY(projectId), id);
}

function readCookie(projectId: string): string | null {
	const prefix = `${COOKIE_KEY(projectId)}=`;
	for (const part of document.cookie.split(';')) {
		const trimmed = part.trim();
		if (trimmed.startsWith(prefix)) {
			const value = decodeURIComponent(trimmed.slice(prefix.length));
			return value || null;
		}
	}
	return null;
}

function writeCookie(projectId: string, id: string) {
	const name = COOKIE_KEY(projectId);
	const maxAge = 365 * 24 * 60 * 60;
	// biome-ignore lint: synchronous cookie API is required here; CookieStore is async and not universally supported
	document.cookie = `${name}=${encodeURIComponent(id)}; Max-Age=${maxAge}; SameSite=Lax; Path=/`;
}

function peekId(projectId: string, mode: VotingMode): string | null {
	if (mode === 'open') return readSession(projectId);
	if (mode === 'cookie') return readCookie(projectId);
	return null;
}

export function useAnonymousId(projectId: string, votingMode: VotingMode) {
	const [queryId, setQueryId] = useState<string | null>(() =>
		peekId(projectId, votingMode),
	);

	useEffect(() => {
		setQueryId(peekId(projectId, votingMode));
	}, [projectId, votingMode]);

	const getOrCreate = useCallback((): string | null => {
		if (votingMode === 'tracked') return null;

		const existing = peekId(projectId, votingMode);
		if (existing) return existing;

		const id =
			votingMode === 'open'
				? `open_${crypto.randomUUID()}`
				: `cookie_${crypto.randomUUID()}`;

		if (votingMode === 'open') {
			writeSession(projectId, id);
		} else {
			writeCookie(projectId, id);
		}
		setQueryId(id);
		return id;
	}, [projectId, votingMode]);

	return { queryId, getOrCreate };
}
